/**
 * PeerJS Hook - Voice Chat + Data Channels
 * Simple P2P voice chat AND game state sync using PeerJS
 * Replaces both usePeerJS (voice) and useWebRTC (data channels)
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import Peer, { MediaConnection, DataConnection } from 'peerjs'

// Export types for compatibility
export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'failed' | 'closed'

export interface VoiceStatus {
  localMuted: boolean
  remoteMuted: boolean
  localConnected: boolean
  remoteConnected: boolean
}

export interface PeerMessage {
  type: 'game_state' | 'move' | 'chat' | 'turn_change' | 'player_action'
  data: any
  timestamp: number
}

interface UsePeerJSProps {
  sessionCode: string
  playerNumber: 1 | 2
  playerName: string
  enabled?: boolean
  autoConnectData?: boolean // NEW: Auto-connect data channel (without mic)
  onMessage?: (message: PeerMessage, fromPlayer: number) => void
  onConnectionChange?: (status: ConnectionStatus, playerId: number) => void
}

export function usePeerJS({
  sessionCode,
  playerNumber,
  playerName,
  enabled = true,
  autoConnectData = true, // NEW: Default to auto-connect
  onMessage,
  onConnectionChange,
}: UsePeerJSProps) {
  const [peer, setPeer] = useState<Peer | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [isMuted, setIsMuted] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('disconnected')
  const [error, setError] = useState<string | null>(null)

  // Voice status tracking (for FloatingVoiceControls)
  const [voiceStatus, setVoiceStatus] = useState<VoiceStatus>({
    localMuted: false,
    remoteMuted: false,
    localConnected: false,
    remoteConnected: false,
  })

  // Voice chat refs
  const callRef = useRef<MediaConnection | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null)
  const isConnectingRef = useRef(false)
  const hasCalledRef = useRef(false)

  // Data channel refs (NEW - for game state sync)
  const dataConnectionRef = useRef<DataConnection | null>(null)
  const hasDataCalledRef = useRef(false)

  // Generate deterministic peer IDs based on session code
  const getPeerId = useCallback((player: 1 | 2) => {
    return `zgames-${sessionCode}-p${player}`
  }, [sessionCode])

  // Setup data channel for game state sync
  const setupDataChannel = useCallback((conn: DataConnection, onOpenCallback?: () => void, onErrorCallback?: (err: Error) => void) => {
    console.log('[PeerJS] Setting up data channel...')

    conn.on('open', () => {
      console.log('[PeerJS] ✅ Data channel opened!')
      setIsConnected(true)
      setConnectionStatus('connected')
      onConnectionChange?.('connected', playerNumber === 1 ? 2 : 1)
      onOpenCallback?.()
    })

    conn.on('data', (data) => {
      try {
        const message = data as PeerMessage
        console.log('[PeerJS] 📨 Received data:', message.type)
        onMessage?.(message, playerNumber === 1 ? 2 : 1)
      } catch (err) {
        console.error('[PeerJS] Failed to parse message:', err)
      }
    })

    conn.on('close', () => {
      console.log('[PeerJS] Data channel closed')
      setIsConnected(false)
      setConnectionStatus('disconnected')
      onConnectionChange?.('disconnected', playerNumber === 1 ? 2 : 1)
      dataConnectionRef.current = null
    })

    conn.on('error', (err) => {
      console.error('[PeerJS] Data channel error:', err)
      setError(err.message || 'Data channel error')
      setConnectionStatus('failed')
      onConnectionChange?.('failed', playerNumber === 1 ? 2 : 1)
      onErrorCallback?.(err)
    })
  }, [onMessage, onConnectionChange, playerNumber])

  // Initialize peer and set up incoming call handler
  useEffect(() => {
    if (!enabled || !sessionCode) return

    console.log('[PeerJS] Initializing peer...', { sessionCode, playerNumber, playerName })

    const myPeerId = getPeerId(playerNumber)
    const newPeer = new Peer(myPeerId, {
      debug: 2, // Log level
    })

    newPeer.on('open', (id) => {
      console.log('[PeerJS] ✅ Peer registered with ID:', id)
      setPeer(newPeer)
      setConnectionStatus('disconnected')
    })

    // IMPORTANT: Set up incoming DATA connection handler immediately
    // This allows us to receive game state even if we haven't clicked voice chat yet
    newPeer.on('connection', (conn) => {
      console.log('[PeerJS] 📦 Incoming data connection received!')

      // Prevent duplicate connections
      if (dataConnectionRef.current) {
        console.log('[PeerJS] Already have a data connection, ignoring new one')
        return
      }

      dataConnectionRef.current = conn
      setupDataChannel(conn)
    })

    // Set up incoming voice call handler
    newPeer.on('call', async (call) => {
      console.log('[PeerJS] 📞 Incoming voice call received!')

      // Prevent answering multiple calls if already connected
      if (callRef.current) {
        console.log('[PeerJS] Already in a voice call, ignoring new incoming call')
        return
      }

      try {
        // Get microphone if we don't have it yet
        if (!streamRef.current) {
          console.log('[PeerJS] Getting microphone for incoming call...')
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: { ideal: true },
              noiseSuppression: { ideal: true },
              autoGainControl: { ideal: true },
              sampleRate: { ideal: 48000 },
              channelCount: { ideal: 1 },
            }
          })
          streamRef.current = stream
        }

        // Answer the call
        call.answer(streamRef.current)
        callRef.current = call
        console.log('[PeerJS] ✅ Answered call with audio stream')
        setConnectionStatus('connecting')

        // Listen for remote stream
        call.on('stream', (remoteStream) => {
          console.log('[PeerJS] ✅ Received remote audio stream')

          // Play remote audio
          if (!remoteAudioRef.current) {
            remoteAudioRef.current = new Audio()
            remoteAudioRef.current.autoplay = true
          }
          remoteAudioRef.current.srcObject = remoteStream

          setIsConnected(true)
          setConnectionStatus('connected')
          setVoiceStatus(prev => ({ ...prev, localConnected: true, remoteConnected: true }))
          console.log('[PeerJS] 🎉 Voice chat connected!')
        })

        call.on('close', () => {
          console.log('[PeerJS] Call closed by remote peer')
          setIsConnected(false)
          setConnectionStatus('disconnected')
          setVoiceStatus(prev => ({ ...prev, localConnected: false, remoteConnected: false }))
        })

        call.on('error', (err) => {
          console.error('[PeerJS] Call error:', err)
          setError(err.message)
          setConnectionStatus('failed')
          setVoiceStatus(prev => ({ ...prev, localConnected: false, remoteConnected: false }))
        })

        // Monitor connection state (access the underlying RTCPeerConnection)
        const peerConnection = (call as any).peerConnection
        if (peerConnection) {
          peerConnection.oniceconnectionstatechange = () => {
            console.log('[PeerJS] ICE connection state:', peerConnection.iceConnectionState)
            if (peerConnection.iceConnectionState === 'disconnected' ||
                peerConnection.iceConnectionState === 'failed') {
              console.warn('[PeerJS] ICE connection lost, call may drop')
            }
          }
        }

      } catch (err) {
        console.error('[PeerJS] Error answering call:', err)
        setError(err instanceof Error ? err.message : 'Failed to answer call')
        setConnectionStatus('failed')
      }
    })

    newPeer.on('error', (err) => {
      console.error('[PeerJS] Peer error:', err)
      setError(err.message)
      setConnectionStatus('failed')
    })

    return () => {
      console.log('[PeerJS] Cleaning up peer')
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
      }
      newPeer.destroy()
    }
  }, [enabled, sessionCode, playerNumber, playerName, getPeerId, setupDataChannel])

  // Auto-connect data channel (without mic) for Player 2
  useEffect(() => {
    if (!autoConnectData || !peer || !sessionCode || playerNumber !== 2) return
    if (hasDataCalledRef.current || dataConnectionRef.current) return

    // CRITICAL: Wait for peer to be fully registered before attempting connection
    if (!peer.open || !peer.id) {
      console.log('[PeerJS] Peer not fully registered yet, waiting...', { open: peer.open, id: peer.id })
      return
    }

    console.log('[PeerJS] Auto-connecting data channel (Player 2)...')
    console.log('[PeerJS] My peer ID:', peer.id)
    console.log('[PeerJS] Peer is open:', peer.open)
    console.log('[PeerJS] Session code:', sessionCode)

    let retryCount = 0
    const maxRetries = 3
    let retryTimeout: NodeJS.Timeout

    const attemptConnection = () => {
      const targetPeerId = getPeerId(1)
      console.log(`[PeerJS] Player 2: Auto-connecting to Player 1... (attempt ${retryCount + 1}/${maxRetries})`, targetPeerId)
      console.log('[PeerJS] Target peer ID:', targetPeerId)

      try {
        const dataConn = peer.connect(targetPeerId, {
          reliable: true,
        })

        if (dataConn) {
          dataConnectionRef.current = dataConn
          hasDataCalledRef.current = true
          setConnectionStatus('connecting')

          // Set a timeout to detect if connection never opens
          const connectionTimeout = setTimeout(() => {
            if (dataConnectionRef.current && !dataConnectionRef.current.open) {
              console.warn('[PeerJS] Data connection timeout - never opened')
              dataConnectionRef.current.close()
              dataConnectionRef.current = null
              hasDataCalledRef.current = false

              // Retry if we haven't exceeded max attempts
              if (retryCount < maxRetries - 1) {
                retryCount++
                console.log(`[PeerJS] Retrying connection in 3 seconds... (${retryCount}/${maxRetries})`)
                retryTimeout = setTimeout(attemptConnection, 3000)
              } else {
                console.error('[PeerJS] Auto-connect failed after 3 attempts')
                setConnectionStatus('failed')
                setError('Failed to connect to peer after 3 attempts')
              }
            }
          }, 10000) // 10 second timeout for connection to open

          // Setup the data channel with success/error handlers
          setupDataChannel(
            dataConn,
            // onOpen callback
            () => {
              clearTimeout(connectionTimeout)
              console.log('[PeerJS] ✅ Data channel auto-connected successfully')
            },
            // onError callback
            (err) => {
              clearTimeout(connectionTimeout)
              console.error('[PeerJS] Data connection error during auto-connect:', err)
              dataConnectionRef.current = null
              hasDataCalledRef.current = false

              // Retry if we haven't exceeded max attempts
              if (retryCount < maxRetries - 1) {
                retryCount++
                console.log(`[PeerJS] Retrying after error in 3 seconds... (${retryCount}/${maxRetries})`)
                retryTimeout = setTimeout(attemptConnection, 3000)
              } else {
                console.error('[PeerJS] Auto-connect failed after 3 attempts')
                setError(err.message || 'Connection failed')
              }
            }
          )

          console.log('[PeerJS] Data connection initiated, waiting for open...')
        } else {
          console.error('[PeerJS] peer.connect() returned null')
          // Retry
          if (retryCount < maxRetries - 1) {
            retryCount++
            retryTimeout = setTimeout(attemptConnection, 3000)
          } else {
            setConnectionStatus('failed')
            setError('Failed to create connection')
          }
        }
      } catch (err) {
        console.error('[PeerJS] Auto-connect error:', err)
        // Retry
        if (retryCount < maxRetries - 1) {
          retryCount++
          retryTimeout = setTimeout(attemptConnection, 3000)
        } else {
          setConnectionStatus('failed')
          setError(err instanceof Error ? err.message : 'Connection failed')
        }
      }
    }

    // Start attempting connection after 2 seconds (wait for Player 1's peer to register)
    const initialDelay = setTimeout(() => {
      attemptConnection()
    }, 2000)

    return () => {
      clearTimeout(initialDelay)
      clearTimeout(retryTimeout)
    }
  }, [autoConnectData, peer, peer?.open, peer?.id, sessionCode, playerNumber, getPeerId, setupDataChannel, onConnectionChange])

  // Connect: Get microphone and initiate call (Player 2 only)
  const connect = useCallback(async () => {
    if (!peer) {
      console.error('[PeerJS] Peer not initialized yet')
      return
    }

    if (isConnectingRef.current) {
      console.log('[PeerJS] Already connecting...')
      return
    }

    // Reset connection state for reconnection
    if (callRef.current) {
      callRef.current.close()
      callRef.current = null
    }
    if (dataConnectionRef.current) {
      dataConnectionRef.current.close()
      dataConnectionRef.current = null
    }
    hasCalledRef.current = false
    hasDataCalledRef.current = false

    try {
      isConnectingRef.current = true
      console.log('[PeerJS] Starting connection (fresh)...')
      setConnectionStatus('connecting')
      setError(null)

      // Get microphone access with enhanced audio processing
      console.log('[PeerJS] Requesting microphone access...')
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: { ideal: true },
          noiseSuppression: { ideal: true },
          autoGainControl: { ideal: true },
          sampleRate: { ideal: 48000 }, // Higher quality audio
          channelCount: { ideal: 1 }, // Mono reduces bandwidth
        }
      })

      streamRef.current = stream
      console.log('[PeerJS] ✅ Microphone access granted with enhanced audio processing')

      if (playerNumber === 1) {
        // Player 1 just waits for Player 2 to connect
        // The incoming handlers are already set up in useEffect
        console.log('[PeerJS] Player 1: Waiting for Player 2 to connect...')
        console.log('[PeerJS] Player 1: Ready to receive connections!')

      } else {
        // Player 2 initiates BOTH voice call AND data connection to Player 1
        const targetPeerId = getPeerId(1)
        console.log('[PeerJS] Player 2: Connecting to Player 1...', targetPeerId)

        // Wait a bit to ensure Player 1's peer is registered
        await new Promise(resolve => setTimeout(resolve, 2000))

        // 1. Establish DATA connection first (works without mic permission)
        if (!hasDataCalledRef.current) {
          console.log('[PeerJS] Player 2: Opening data connection...')
          const dataConn = peer.connect(targetPeerId, {
            reliable: true, // Ensure ordered delivery
          })

          if (!dataConn) {
            throw new Error('Failed to initiate data connection - peer may not be registered')
          }

          dataConnectionRef.current = dataConn
          hasDataCalledRef.current = true
          setupDataChannel(dataConn)
          console.log('[PeerJS] Player 2: ✅ Data connection initiated')
        }

        // 2. Establish VOICE call (only if we have microphone)
        if (!hasCalledRef.current && stream) {
          console.log('[PeerJS] Player 2: Starting voice call...')
          const call = peer.call(targetPeerId, stream)

          if (!call) {
            console.warn('[PeerJS] Failed to initiate voice call - continuing with data only')
          } else {
            callRef.current = call
            hasCalledRef.current = true
            console.log('[PeerJS] Player 2: ✅ Voice call initiated')

            // Listen for remote stream
            call.on('stream', (remoteStream) => {
              console.log('[PeerJS] Player 2: ✅ Received remote stream')

              // Play remote audio
              if (!remoteAudioRef.current) {
                remoteAudioRef.current = new Audio()
                remoteAudioRef.current.autoplay = true
              }
              remoteAudioRef.current.srcObject = remoteStream

              setVoiceStatus(prev => ({ ...prev, localConnected: true, remoteConnected: true }))
              console.log('[PeerJS] 🎉 Voice chat connected!')
            })

            call.on('close', () => {
              console.log('[PeerJS] Voice call closed by remote peer')
              setVoiceStatus(prev => ({ ...prev, localConnected: false, remoteConnected: false }))
              isConnectingRef.current = false
            })

            call.on('error', (err) => {
              console.error('[PeerJS] Voice call error:', err)
              setError(err.message)
              setVoiceStatus(prev => ({ ...prev, localConnected: false, remoteConnected: false }))
              isConnectingRef.current = false
            })

            // Monitor connection state (access the underlying RTCPeerConnection)
            const peerConnection = (call as any).peerConnection
            if (peerConnection) {
              peerConnection.oniceconnectionstatechange = () => {
                console.log('[PeerJS] ICE connection state (Player 2):', peerConnection.iceConnectionState)
                if (peerConnection.iceConnectionState === 'disconnected' ||
                    peerConnection.iceConnectionState === 'failed') {
                  console.warn('[PeerJS] ICE connection lost, voice call may drop')
                  isConnectingRef.current = false
                }
              }
            }
          }
        } else if (!stream) {
          console.log('[PeerJS] Player 2: No mic stream, skipping voice call (data connection only)')
        }
      }

    } catch (err) {
      console.error('[PeerJS] Connection error:', err)
      setError(err instanceof Error ? err.message : 'Connection failed')
      setConnectionStatus('failed')
      isConnectingRef.current = false
    }
  }, [peer, playerNumber, getPeerId])

  // Disconnect
  const disconnect = useCallback(() => {
    console.log('[PeerJS] Disconnecting...')

    // Close data connection
    if (dataConnectionRef.current) {
      dataConnectionRef.current.close()
      dataConnectionRef.current = null
    }

    // Close voice call
    if (callRef.current) {
      callRef.current.close()
      callRef.current = null
    }

    // Stop microphone
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }

    // Stop remote audio
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null
    }

    setIsConnected(false)
    setConnectionStatus('disconnected')
    setVoiceStatus({
      localMuted: false,
      remoteMuted: false,
      localConnected: false,
      remoteConnected: false,
    })
    isConnectingRef.current = false
    hasCalledRef.current = false
    hasDataCalledRef.current = false // Reset so we can reconnect
  }, [])

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (streamRef.current) {
      const audioTracks = streamRef.current.getAudioTracks()
      audioTracks.forEach(track => {
        track.enabled = !track.enabled
      })
      const newMutedState = !isMuted
      setIsMuted(newMutedState)
      setVoiceStatus(prev => ({ ...prev, localMuted: newMutedState }))
      console.log('[PeerJS] Mute toggled:', newMutedState)
    }
  }, [isMuted])

  // Send message via data channel (for game state sync)
  const sendMessage = useCallback((type: PeerMessage['type'], data: any): boolean => {
    if (!dataConnectionRef.current || dataConnectionRef.current.open !== true) {
      console.warn('[PeerJS] Data connection not open, message not sent')
      return false
    }

    const message: PeerMessage = {
      type,
      data,
      timestamp: Date.now(),
    }

    try {
      dataConnectionRef.current.send(message)
      console.log('[PeerJS] 📤 Sent message:', type)
      return true
    } catch (err) {
      console.error('[PeerJS] Send message error:', err)
      return false
    }
  }, [])

  // Network change detection and auto-reconnection
  useEffect(() => {
    if (!enabled || !sessionCode) return

    let reconnectTimeout: NodeJS.Timeout
    let connectionCheckInterval: NodeJS.Timeout

    // Track if we should attempt reconnection
    const wasConnectedRef = { current: false }

    // Update wasConnected flag when connection status changes
    if (isConnected) {
      wasConnectedRef.current = true
    }

    const attemptReconnect = () => {
      console.log('[PeerJS] Attempting to reconnect after network change...')

      // Destroy the existing peer completely
      if (peer) {
        try {
          peer.destroy()
        } catch (err) {
          console.error('[PeerJS] Error destroying peer:', err)
        }
      }

      // Close existing connections gracefully
      if (dataConnectionRef.current) {
        try {
          dataConnectionRef.current.close()
        } catch (err) {
          console.error('[PeerJS] Error closing data connection:', err)
        }
        dataConnectionRef.current = null
      }

      if (callRef.current) {
        try {
          callRef.current.close()
        } catch (err) {
          console.error('[PeerJS] Error closing call:', err)
        }
        callRef.current = null
      }

      // Reset flags
      hasCalledRef.current = false
      hasDataCalledRef.current = false
      isConnectingRef.current = false
      setConnectionStatus('connecting')

      // Force re-initialization by triggering parent component to recreate the hook
      // This will be handled by the parent re-mounting or the peer re-initializing
      reconnectTimeout = setTimeout(() => {
        console.log('[PeerJS] Re-establishing connection...')
        connect()
      }, 3000)
    }

    const handleOnline = () => {
      console.log('[PeerJS] 🌐 Network online detected')

      // If we were connected before, try to reconnect
      if (wasConnectedRef.current || streamRef.current || hasCalledRef.current || hasDataCalledRef.current) {
        attemptReconnect()
      }
    }

    const handleOffline = () => {
      console.log('[PeerJS] 📴 Network offline detected')
      setIsConnected(false)
      setConnectionStatus('disconnected')
      onConnectionChange?.('disconnected', playerNumber === 1 ? 2 : 1)
    }

    // Monitor connection health (check every 5 seconds)
    connectionCheckInterval = setInterval(() => {
      // Check data connection health
      if (dataConnectionRef.current) {
        const conn = dataConnectionRef.current
        // @ts-ignore - access internal peerConnection
        const pc = conn.peerConnection

        if (pc) {
          const state = pc.iceConnectionState
          console.log('[PeerJS] ICE connection state check:', state)

          if (state === 'disconnected' || state === 'failed' || state === 'closed') {
            console.warn('[PeerJS] Connection lost, attempting reconnect...')
            setIsConnected(false)
            attemptReconnect()
          }
        }
      }
    }, 5000)

    // Listen for online/offline events
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Also listen for visibility change (mobile app switching)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isConnected && wasConnectedRef.current) {
        console.log('[PeerJS] App became visible, checking connection...')
        // Check if we need to reconnect after app was backgrounded
        if (!dataConnectionRef.current || !dataConnectionRef.current.open) {
          console.log('[PeerJS] Connection lost while backgrounded, reconnecting...')
          attemptReconnect()
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearTimeout(reconnectTimeout)
      clearInterval(connectionCheckInterval)
    }
  }, [enabled, sessionCode, connect, isConnected, playerNumber, onConnectionChange, peer])

  return {
    connectionStatus,
    isConnected,
    isMuted,
    voiceStatus, // NEW - for FloatingVoiceControls
    error,
    connect,
    disconnect,
    toggleMute,
    sendMessage, // NEW - for game state sync
  }
}
